import { elevationClassName } from '@creditornot/cb-ingredients';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { Input } from '@creditornot/cb-input';
import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Typography } from 'common/typography';

import { EntityActionItem } from './EntityActionItem';
import { EntityActionItemTitle } from './EntitySelector.styled';
import { useExperienceModuleContext } from '../../../context';
import { ExperienceEntity, ExperienceEntityTree, ExperienceEntityType } from '../../../types';
import { buildNewExperienceEntityTreeFilteredByName } from '../../../utils';

const DropdownContainer = styled.div.attrs({ className: elevationClassName.xsmall })`
  display: inline-flex;
  padding: 16px;
  width: 350px;
  max-height: 400px;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  border-radius: 8px;
  background: ${color.bg};
  border: 1px solid ${color.border};
  overflow-y: auto;
`;

export const EntitySelectorOverlay = ({
  isVenueSpecific,
  handleEntityChange,
}: {
  isVenueSpecific?: boolean;
  handleEntityChange: () => void;
}) => {
  const { locale } = useIntl();
  const [query, setQuery] = useState('');
  const {
    experienceEntity,
    experienceEntityTree,
    selectedExperienceEntity,
    setSelectedExperienceEntity,
    selectedExperienceVenueEntity,
    setSelectedExperienceVenueEntity,
  } = useExperienceModuleContext();

  const handleClickEntity = (experienceEntity: ExperienceEntity) => {
    setSelectedExperienceEntity(experienceEntity);
    if (experienceEntity.experienceEntityType === ExperienceEntityType.VENUE) {
      setSelectedExperienceVenueEntity(experienceEntity);
    }
    handleEntityChange();
  };

  const experienceEntityTreeChildren: ExperienceEntityTree[] = useMemo(() => {
    const defaultChildren =
      isVenueSpecific && experienceEntity.experienceEntityType === ExperienceEntityType.ENTERPRISE
        ? experienceEntityTree.children.flatMap(experienceEntityTree => experienceEntityTree.children)
        : experienceEntityTree.children;
    const sanitizedQuery = query && query.trim() !== '' ? query : undefined;
    return sanitizedQuery
      ? buildNewExperienceEntityTreeFilteredByName(defaultChildren, sanitizedQuery, locale)
      : defaultChildren;
  }, [experienceEntityTree, experienceEntity, isVenueSpecific, query, locale]);

  return (
    <DropdownContainer>
      <Input placeholder="Search" value={query} onChange={event => setQuery(event.target.value)} />
      {!isVenueSpecific && (
        <EntityActionItemTitle
          isSelected={experienceEntity.experienceEntityId === selectedExperienceEntity.experienceEntityId}
          onClick={() => handleClickEntity(experienceEntity)}
        >
          <Typography.Body3>
            <FormattedMessage id="experience.search.filter.all" />
          </Typography.Body3>
        </EntityActionItemTitle>
      )}
      <EntityActionItem
        experienceEntityTreeChildren={experienceEntityTreeChildren}
        level={0}
        selectedExperienceEntity={
          isVenueSpecific && selectedExperienceVenueEntity ? selectedExperienceVenueEntity : selectedExperienceEntity
        }
        handleClickEntity={handleClickEntity}
        searchValue={query}
      />
    </DropdownContainer>
  );
};
